import { Popover, PopoverTrigger } from "@/components/ui/popover";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { toLocalIso, fromLocalIso, formatLocalDay, dateInRanges, localToday } from "@/lib/localDate";

const formatDisplay = (iso) => formatLocalDay(iso, { fallback: "Pick a date" });

export function SingleDatePicker({
  label,
  labelIcon: LabelIcon,
  labelIconColor = "text-neutral-500",
  value,
  onChange,
  min,
  bookedRanges = [],
  // Iter 67en+++++++ — soft-amber overlay for the renter's OWN active
  // bookings (across all vendors). Not disabled — they can still pick
  // these dates and continue, but visually flagged so they notice the
  // overlap before hitting "Book now".
  myBookedRanges = [],
  testId,
}) {
  // Iter 67dk — control open state so we can auto-close the popover the
  // moment the renter taps a date (especially needed on mobile/tablet, where
  // the popover was previously sitting open over adjacent fields).
  const [open, setOpen] = useState(false);
  const selected = fromLocalIso(value);
  const minDate = (min && fromLocalIso(min)) || localToday();

  // Build a date-by-date matcher for the calendar's `modifiers` API.
  // dateInRanges already returns boolean, so wrap it.
  const isMyOverlap = (date) => dateInRanges(date, myBookedRanges);
  return (
    <div>
      {label && (
        <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
          {LabelIcon && <LabelIcon className={`h-3 w-3 ${labelIconColor}`} aria-hidden="true" />}
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="mt-1 w-full inline-flex items-center justify-between rounded-xl border border-input bg-white px-3 py-2 text-sm hover:border-neutral-400 transition focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            data-testid={testId}
          >
            <span className={value ? "text-neutral-900" : "text-neutral-400"}>{formatDisplay(value)}</span>
            <CalendarIcon className="h-4 w-4 text-neutral-500" />
          </button>
        </PopoverTrigger>
        {/* Iter 67dm — render the calendar INLINE (no Portal) so it stays
            inside the parent Radix Dialog/Sheet on touch devices. The shadcn
            PopoverContent wraps in <Portal> which renders the calendar to
            document.body — on iOS Safari that breaks the touch chain inside
            the mobile booking bottom-sheet and the date button silently
            no-ops. Using PopoverPrimitive.Content directly avoids the portal
            entirely. */}
        <PopoverPrimitive.Content
          align="start"
          sideOffset={8}
          collisionPadding={12}
          avoidCollisions
          className="z-50 w-auto rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (!d) return;
              onChange(toLocalIso(d));
              setOpen(false);
            }}
            disabled={(date) => date < minDate || dateInRanges(date, bookedRanges)}
            modifiers={{ myBooked: isMyOverlap }}
            modifiersClassNames={{
              myBooked:
                "bg-amber-100 text-amber-900 font-semibold ring-1 ring-amber-300 ring-inset rounded-md",
            }}
            initialFocus
          />
        </PopoverPrimitive.Content>
      </Popover>
    </div>
  );
}
